import React, { useEffect, useState, Suspense, lazy } from 'react';
import './App.css';
// CRITICAL: Do not remove this import! Modal.css contains critical styles for the appointment modal overlay
// that must be loaded with the main app bundle to prevent CSS from being lost during build optimization.
// See MODAL_CSS_FIX.md for details.
import './Modal.css';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { confirmAppointment, pollUnconfirmedAppointments, refuseAppointment } from './api/apiHelper';
import { getSuggestedPrice, SuggestedPriceResponse } from './api/apiHelperFinancials';
import axiosConnector from './api/axiosConnector';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { UserProvider, useUser } from './contexts/UserContext';
import { ServerStatusProvider } from './contexts/ServerStatusContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ModalProvider, useModal } from './contexts/ModalContext';
import ServerErrorIndicator from './components/ServerErrorIndicator';
import { socketIOService } from './services/websocket';
import OfflineIndicator from './components/OfflineIndicator';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import LoadingFallback from './components/LoadingFallback';
import UpdateNotification from './components/UpdateNotification';
import { useServiceWorkerUpdate } from './hooks/useServiceWorkerUpdate';
import PreprodBanner from './components/PreprodBanner';

// Lazy load components for better performance
const Training = lazy(() => import('./pages/Training'));
const Software = lazy(() => import('./pages/Software'));
const Platform = lazy(() => import('./pages/Platform'));
const UserAgenda = lazy(() => import('./pages/UserAgenda'));
const AdminAgenda = lazy(() => import('./pages/AdminAgenda'));
const Agenda = lazy(() => import('./pages/Agenda'));
const ServiceOrder = lazy(() => import('./pages/ServiceOrder'));
const Login = lazy(() => import('./pages/Login'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const Approvals = lazy(() => import('./pages/Approvals'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Profile = lazy(() => import('./pages/Profile'));
const Points = lazy(() => import('./pages/Points'));
const Notifications = lazy(() => import('./pages/Notifications'));
const EquipmentManagement = lazy(() => import('./pages/EquipmentManagement'));
const CustomerManagement = lazy(() => import('./pages/CustomerManagement'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const TechnicianManagement = lazy(() => import('./pages/TechnicianManagement'));
const TechnicianDetail = lazy(() => import('./pages/TechnicianDetail'));
const TechnicianParts = lazy(() => import('./pages/TechnicianParts'));
const AdminParts = lazy(() => import('./pages/AdminParts'));
const AdminManagement = lazy(() => import('./pages/AdminManagement'));
const DangerZone = lazy(() => import('./pages/DangerZone'));
const Financials = lazy(() => import('./pages/Financials'));
const PricingConfig = lazy(() => import('./pages/PricingConfig'));
const QRScanner = lazy(() => import('./components/QRScanner'));
const NotFound = lazy(() => import('./pages/NotFound'));

let rePollUnconfirmedAppointments = false;

const triggerPollUnconfirmedAppointments = () => {
  rePollUnconfirmedAppointments = !rePollUnconfirmedAppointments;
};

const SCENARIO_LABELS: Record<string, string> = {
  personal_agenda: 'Agenda Pessoal',
  independent_client: 'Cliente Independente',
  locadora_client_opens: 'Locadora (cliente)',
  locadora_admin_opens: 'Locadora (admin)',
  urgent_locadora: 'Locadora Urgente',
};

const DAY_TYPE_LABELS: Record<string, string> = {
  weekday: 'Dia útil',
  weekend: 'Fim de semana',
};

const RADIUS_TIER_LABELS: Record<string, string> = {
  center: 'Centro',
  mid: 'Raio médio',
  far: 'Raio externo',
};

const Modal: React.FC<{ appointment: any }> = ({ appointment }) => {
  console.log('🎭 [MODAL COMPONENT] Modal component rendered with appointment:', appointment?.id);

  const { closeModal } = useModal();
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [pricingData, setPricingData] = useState<SuggestedPriceResponse | null>(null);
  const [showPricingTooltip, setShowPricingTooltip] = useState(false);
  const { user } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (appointment?.id) {
      getSuggestedPrice(appointment.id, user?.id ?? undefined).then(setPricingData).catch((err) => console.error('[pricing] getSuggestedPrice failed:', (err as any)?.response?.data ?? err));
    }
  }, [appointment?.id]);

  console.log('🎭 [MODAL COMPONENT] Modal validation check:', {
    hasAppointment: !!appointment,
    hasAppointmentId: !!appointment?.id,
    isArray: Array.isArray(appointment),
    hasUser: !!user,
    hasUserId: !!user?.id,
    willRender: appointment && appointment.id && !Array.isArray(appointment) && user && user.id,
  });

  if (!appointment || !appointment.id || Array.isArray(appointment) || !user || !user.id) {
    console.log('⚠️ [MODAL COMPONENT] Modal component returning null - validation failed');
    return null;
  }

  console.log('✅ [MODAL COMPONENT] Modal component passed validation, rendering UI...');

  const getCustomerName = () => {
    try {
      console.log('🔍 [CUSTOMER NAME DEBUG] Full appointment object:', appointment);
      console.log('🔍 [CUSTOMER NAME DEBUG] is_batch:', appointment.is_batch);
      console.log('🔍 [CUSTOMER NAME DEBUG] customer_name field:', appointment.customer_name);

      if (appointment.custom_equipment && !appointment.equipment_id) {
        return appointment.solicitante || 'N/A';
      }

      // For batch appointments, use the customer_name field from backend
      if (appointment.is_batch) {
        const customerName = appointment.customer_name;
        console.log('🔍 [CUSTOMER NAME DEBUG] Batch customer_name:', customerName);
        if (customerName) return customerName;
      }

      // For regular appointments
      const equipmentCustomerName = appointment.equipment?.building?.customer?.name;
      console.log('🔍 [CUSTOMER NAME DEBUG] equipmentCustomerName:', equipmentCustomerName);
      if (equipmentCustomerName) {
        return equipmentCustomerName;
      }
      const clientName = appointment.client?.name;
      console.log('🔍 [CUSTOMER NAME DEBUG] clientName:', clientName);
      if (clientName) {
        return clientName;
      }
      console.log('🔍 [CUSTOMER NAME DEBUG] Returning N/A');
      return 'N/A';
    } catch (error) {
      console.error('Error getting customer name:', error);
      return 'N/A';
    }
  };

  const getAdminName = () => {
    try {
      console.log('🔍 [ADMIN NAME DEBUG] is_batch:', appointment.is_batch);
      console.log('🔍 [ADMIN NAME DEBUG] admin_name field:', appointment.admin_name);

      // Use admin_name field from backend for both batch and regular appointments
      const adminName = appointment.admin_name;
      console.log('🔍 [ADMIN NAME DEBUG] adminName:', adminName);
      if (adminName) return adminName;

      console.log('🔍 [ADMIN NAME DEBUG] Returning null');
      return null;
    } catch (error) {
      console.error('Error getting admin name:', error);
      return null;
    }
  };

  const getEquipmentDisplay = () => {
    if (appointment.is_batch) {
      return 'Vários Equipamentos';
    }

    if (appointment.custom_equipment && !appointment.equipment_id) {
      const typeName =
        appointment.custom_equipment.equipment_type_name ||
        appointment.custom_equipment.custom_type_text ||
        'DESCONHECIDO';
      const name = appointment.custom_equipment.name || 'N/A';
      return `${typeName} - ${name}`;
    }

    const type = appointment.equipment?.type?.description || 'DESCONHECIDO';
    const brand = appointment.equipment?.brand || 'N/A';
    const model = appointment.equipment?.model || 'N/A';
    const serialNumber = appointment.equipment?.serial_number;
    const serial = serialNumber ? ` (${serialNumber})` : '';

    return `${type}, ${brand}, ${model}${serial}`;
  };

  const customerName = getCustomerName();
  const adminName = getAdminName();
  const equipmentDisplay = getEquipmentDisplay();

  const timeSlotNames = {
    'morning': 'Manhã',
    'afternoon': 'Tarde',
    'night': 'Noite'
  };

  const formatDate = (date: string) => {
    if (date.length === 10 && date.includes('-')) {
      return date.split('-').reverse().join('/');
    }
    return date;
  };
  return (
          <div className="modal-overlay" onClick={closeModal}>
        <div className="modal-content dark-theme" onClick={(e) => e.stopPropagation()}>
          <span className="close" onClick={closeModal}>&times;</span>
        <h2>Nova solicitação de agendamento</h2>
        <ul style={{ listStyle: 'none', padding: '10px', textAlign: 'left' }}>
          <li>Cliente: {customerName}</li>
          {adminName && <li>Admin: {adminName}</li>}
          <li>
            Endereço: {appointment.custom_address || appointment.address?.streetAndNumber || ''}
            {!appointment.custom_address ? appointment.client?.address?.city || '' : ''}
          </li>
          <li>Equipo: {equipmentDisplay}</li>
          <li>Tipo problema: {appointment.tipo_problema_relatado}</li>
          <li>Data: {formatDate(appointment.scheduled_date)}</li>
          <li>Hora: {timeSlotNames[appointment.scheduled_time as keyof typeof timeSlotNames]}</li>
          {pricingData?.hasConfig && pricingData.techAmount != null && pricingData.isIndependentTechnician && (
            <li style={{ marginTop: '10px', fontWeight: 'bold', fontSize: '15px' }}>
              Você receberá: R$ {Number(pricingData.techAmount).toFixed(2)}
              <span
                style={{ marginLeft: '6px', cursor: 'pointer', opacity: 0.7, fontSize: '14px' }}
                onClick={() => setShowPricingTooltip(t => !t)}
                title="Como esse valor foi calculado"
              >ℹ️</span>
              {showPricingTooltip && (
                <div style={{ fontSize: '12px', fontWeight: 'normal', marginTop: '6px', padding: '8px', background: 'rgba(255,255,255,0.12)', borderRadius: '6px', lineHeight: '1.6' }}>
                  {pricingData.scenarioType && <div>Cenário: {SCENARIO_LABELS[pricingData.scenarioType] ?? pricingData.scenarioType}</div>}
                  {pricingData.debugContext?.urgencyType && pricingData.debugContext.urgencyType !== 'normal' && (
                    <div>Urgência: {pricingData.debugContext.urgencyType}</div>
                  )}
                  {pricingData.debugContext?.city && <div>Cidade: {pricingData.debugContext.city}</div>}
                  {pricingData.debugContext?.dayType && <div>Dia: {DAY_TYPE_LABELS[pricingData.debugContext.dayType] ?? pricingData.debugContext.dayType}</div>}
                  {pricingData.debugContext?.radiusTier && <div>Raio: {RADIUS_TIER_LABELS[pricingData.debugContext.radiusTier] ?? pricingData.debugContext.radiusTier}</div>}
                  {pricingData.suggestedPrice != null && (
                    <div>
                      Preço sugerido: R$ {Number(pricingData.suggestedPrice).toFixed(2)}
                      {pricingData.techAmount != null && pricingData.manuAmount != null && (
                        <span style={{ opacity: 0.75 }}> (R$ {Number(pricingData.techAmount).toFixed(2)} técnico, R$ {Number(pricingData.manuAmount).toFixed(2)} manu)</span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </li>
          )}
        </ul>
        <div className="button-container">
          <button
            className="cancel-button"
            disabled={buttonsDisabled}
            onClick={() => {
              refuseAppointment(appointment.id, user.id || 0)
                .then(() => {
                  toast.success('Chamado recusado', { autoClose: 1000, hideProgressBar: true });
                })
                .catch((error) => {
                  toast.error('Erro ao recusar chamado');
                }).finally(() => {
                  closeModal();
                  setButtonsDisabled(false);
                  triggerPollUnconfirmedAppointments();
                });
            }}>Recusar</button>
          <button
            className="confirm-button"
            disabled={buttonsDisabled}
            onClick={() => {
              // Disable buttons immediately to prevent rapid clicking
              setButtonsDisabled(true);
              confirmAppointment(appointment.id, user.id || 0)
                .then(() => {
                  toast.success('Chamado confirmado', { autoClose: 1000, hideProgressBar: true });
                  setTimeout(() => {
                    closeModal();
                    setButtonsDisabled(false);
                    // Navigate to agenda and trigger refetch via window event
                    const currentPath = window.location.pathname;
                    if (currentPath.endsWith('/platform/agenda')) {
                      // Already on agenda, just trigger a refetch
                      window.dispatchEvent(new CustomEvent('agendaRefresh'));
                    } else {
                      navigate('/platform/agenda');
                    }
                  }, 1000);
                })
                .catch((error) => {
                  console.error(`❌ [FRONTEND-DEBUG] CONFIRM API ERROR - Appointment: ${appointment.id}, Error:`, error?.response?.data || error.message);
                  if (error.response.status === 400 && error.response.data.message === 'ALREADY_CONFIRMED') {
                    toast.error('Chamado já confirmado por outro técnico');
                    setTimeout(() => {
                      closeModal();
                      setButtonsDisabled(false);
                    }, 1000);
                  } else if (error.response.status === 400 && error.response.data.message === 'MAX_SLOTS_ATTAINED') {
                    toast.error('Nao ha mais vagas disponiveis para esse slot');
                    refuseAppointment(appointment.id, user.id || 0);
                    setTimeout(() => {
                      closeModal();
                      setButtonsDisabled(false);
                    }, 1000);
                  } else {
                    toast.error('Erro ao confirmar chamado');
                    // Reset button state on unexpected errors
                    setButtonsDisabled(false);
                  }
                }).finally(() => {
                  triggerPollUnconfirmedAppointments();
                });
            }}>Aceitar</button>
        </div>
      </div>
    </div>
  );
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useUser();
  const location = useLocation();

  if (!isAuthenticated) {
    // Save the intended destination before redirecting to login
    localStorage.setItem('redirectPath', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const TechnicianOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useUser();
  const location = useLocation();

  if (!isAuthenticated) {
    // Save the intended destination before redirecting to login
    localStorage.setItem('redirectPath', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'technician') {
    return <Navigate to="/404" state={{ reason: `TechnicianOnlyRoute bloqueou: role="${user?.role}" em ${location.pathname}` }} replace />;
  }

  return <>{children}</>;
};

const AdminOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useUser();
  const location = useLocation();

  if (!isAuthenticated) {
    // Save the intended destination before redirecting to login
    localStorage.setItem('redirectPath', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  if (!['admin', 'admin-of-admins'].includes(user?.role || '')) {
    return <Navigate to="/404" state={{ reason: `AdminOnlyRoute bloqueou: role="${user?.role}" em ${location.pathname}` }} replace />;
  }

  return <>{children}</>;
};

const MasterAdminOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useUser();
  const location = useLocation();

  if (!isAuthenticated) {
    localStorage.setItem('redirectPath', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin' || !user?.master_admin) {
    return <Navigate to="/404" state={{ reason: `MasterAdminOnlyRoute bloqueou: role="${user?.role}" master_admin=${user?.master_admin} em ${location.pathname}` }} replace />;
  }

  return <>{children}</>;
};

const SuperAdminOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useUser();
  const location = useLocation();

  if (!isAuthenticated) {
    localStorage.setItem('redirectPath', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  if (!user?.superadmin) {
    return <Navigate to="/404" state={{ reason: `SuperAdminOnlyRoute bloqueou: role="${user?.role}" superadmin=${user?.superadmin} em ${location.pathname}` }} replace />;
  }

  console.log('SUPERADMIN ROUTE PASS', { role: user?.role, superadmin: user?.superadmin, path: location.pathname });
  return <>{children}</>;
};

const ClientOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useUser();
  const location = useLocation();

  if (!isAuthenticated) {
    // Save the intended destination before redirecting to login
    localStorage.setItem('redirectPath', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'user-internal' && user?.role !== 'user-external') {
    return <Navigate to="/404" state={{ reason: `ClientOnlyRoute bloqueou: role="${user?.role}" em ${location.pathname}` }} replace />;
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const { user, isAuthenticated, updateLastVisitedPath } = useUser();

  useEffect(() => {
    if (isAuthenticated && user && user.id) {
      socketIOService.joinRoom(user.id);
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (isAuthenticated && user && user.id) {
      // Track the current path for authenticated users
      updateLastVisitedPath(location.pathname + location.search);
    }
  }, [isAuthenticated, user?.id, location.pathname, location.search]);

  return (
    <>
      <Suspense fallback={<LoadingFallback message="Carregando página..." />}>
        <Routes>
          <Route path="/" element={!isAuthenticated ? <Navigate to="/login" replace /> : <Navigate to="/platform" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/training" element={<div style={{ padding: '20px', textAlign: 'center' }}><h1>Training</h1></div>} />
          <Route path="/software" element={<div style={{ padding: '20px', textAlign: 'center' }}><h1>Software</h1></div>} />
          <Route path="/platform" element={<ProtectedRoute><Platform /></ProtectedRoute>} />
          <Route path="/platform/training" element={<ProtectedRoute><Training /></ProtectedRoute>} />
          <Route path="/platform/software" element={<ProtectedRoute><Software /></ProtectedRoute>} />
          <Route path="/platform/user-agenda" element={<ClientOnlyRoute><UserAgenda /></ClientOnlyRoute>} />
          <Route path="/platform/agenda" element={<TechnicianOnlyRoute><Agenda /></TechnicianOnlyRoute>} />
          <Route path="/platform/admin-agenda" element={<AdminOnlyRoute><AdminAgenda /></AdminOnlyRoute>} />
          <Route path="/platform/admin-settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
          <Route path="/platform/equipment" element={<ProtectedRoute><EquipmentManagement /></ProtectedRoute>} />
          <Route path="/platform/equipments" element={<Navigate to="/platform/equipment" replace />} />
          <Route path="/platform/activity-log" element={<Navigate to="/404" state={{ reason: 'Rota /platform/activity-log foi removida' }} replace />} />
          <Route path="/platform/clients" element={<ProtectedRoute><CustomerManagement /></ProtectedRoute>} />
          <Route path="/platform/usuarios" element={<AdminOnlyRoute><UserManagement /></AdminOnlyRoute>} />
          <Route path="/platform/technicians" element={<ProtectedRoute><TechnicianManagement /></ProtectedRoute>} />
          <Route path="/platform/technicians/:id" element={<ProtectedRoute><TechnicianDetail /></ProtectedRoute>} />
          <Route path="/platform/approvals" element={<ProtectedRoute><Approvals /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/platform/points" element={<TechnicianOnlyRoute><Points /></TechnicianOnlyRoute>} />
          <Route path="/platform/parts" element={<ProtectedRoute><TechnicianParts /></ProtectedRoute>} />
          <Route path="/platform/admin-parts" element={<ProtectedRoute><AdminParts /></ProtectedRoute>} />
          <Route path="/platform/admins" element={<MasterAdminOnlyRoute><AdminManagement /></MasterAdminOnlyRoute>} />
          <Route path="/platform/danger-zone" element={<SuperAdminOnlyRoute><DangerZone /></SuperAdminOnlyRoute>} />
          <Route path="/platform/financials" element={<ProtectedRoute><Financials /></ProtectedRoute>} />
          <Route path="/platform/pricing-config" element={<SuperAdminOnlyRoute><PricingConfig /></SuperAdminOnlyRoute>} />
          <Route path="/platform/qr-scan" element={<ProtectedRoute><QRScanner standalone={true} /></ProtectedRoute>} />
          <Route path="/service-order/:id" element={<ProtectedRoute><ServiceOrder /></ProtectedRoute>} />
          <Route path="/404" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
          <Route path="*" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
        </Routes>
      </Suspense>
    </>
  );
};

const AppWrapper: React.FC = () => {
  const [unconfirmedAppointment, setUnconfirmedAppointment] = useState<any>(null);
  const { showModal, setShowModal, closeModal } = useModal();
  const [verificationStatus, setVerificationStatus] = useState('pending');
  const location = useLocation();
  const { user } = useUser();
  const { showUpdateNotification, performUpdate, dismissUpdate } = useServiceWorkerUpdate();

  const fetchVerificationStatus = async () => {
    if (user?.id && user?.role === 'technician') {
      try {
        const response = await axiosConnector.get(`/users/${user.id}/verification-status`);
        setVerificationStatus((response.data as any).id_verification_status);
      } catch (error) {
        console.error('Error fetching verification status:', error);
      }
    }
  };

  const pollUnconfirmedAppointmentsHandler = () => {
    console.log('🔍 [MODAL DEBUG] pollUnconfirmedAppointmentsHandler called', {
      userRole: user?.role,
      userId: user?.id,
      currentPath: location.pathname,
    });

    if (user?.role === 'technician') {
      pollUnconfirmedAppointments()
        .then((appointments) => {
          console.log('🔍 [MODAL DEBUG] Poll result:', {
            appointmentsCount: appointments.length,
            appointments: appointments,
          });

          if (appointments.length > 0) {
            console.log('✅ [MODAL DEBUG] Opening modal with appointment:', appointments[0].id);
            setUnconfirmedAppointment(appointments[0]);
            setShowModal(true);
          } else {
            console.log('⚠️ [MODAL DEBUG] No unconfirmed appointments, closing modal');
            setUnconfirmedAppointment(null);
            setShowModal(false);
          }
        })
        .catch((error) => {
          console.error('❌ [MODAL DEBUG] Error polling unconfirmed appointments:', error);
          setUnconfirmedAppointment(null);
          setShowModal(false);
        });
    } else {
      console.log('⚠️ [MODAL DEBUG] User is not a technician, skipping poll');
    }
  }

  useEffect(() => {
    pollUnconfirmedAppointmentsHandler();
    fetchVerificationStatus();
  }, [user]);

  useEffect(() => {
    if (user && user.id && location.pathname !== '/platform/user-agenda' && user.role === 'technician') {
      const handleSocketEvent = () => {
        pollUnconfirmedAppointmentsHandler();
      };
      
      const handleNewAppointmentEvent = () => {
        console.log('🔔 [MODAL DEBUG] newAppointmentForTechnician event received, polling in 1 second...');
        setTimeout(() => {
          console.log('🔔 [MODAL DEBUG] Executing delayed poll...');
          pollUnconfirmedAppointmentsHandler();
        }, 1000);
      };

      const handleAppointmentUpdatedEvent = () => {
        setTimeout(() => {
          pollUnconfirmedAppointmentsHandler();
        }, 1000);
      };

      window.addEventListener('newAppointmentForTechnician', handleNewAppointmentEvent);
      window.addEventListener('appointmentUpdatedForTechnician', handleAppointmentUpdatedEvent);
      
      const unsub2 = socketIOService.subscribeCalendarUpdate((data: any) => {
        if (data.type === 'appointment_confirmed' && showModal && unconfirmedAppointment && data.appointmentId === unconfirmedAppointment.id) {
          closeModal();
          return;
        }
        pollUnconfirmedAppointmentsHandler();
      });
      
      // Listen for verification status updates
      const unsub3 = socketIOService.subscribeToEvent('verification_status_updated', (data: any) => {
        closeModal();
        // Trigger banner refresh
        window.dispatchEvent(new CustomEvent('refreshVerificationStatus'));
      });

      // Listen for dismiss_appointment_popup event
      const unsub4 = socketIOService.subscribeToEvent('dismiss_appointment_popup', (data: any) => {
        console.log('🔔 Received dismiss_appointment_popup event:', data);
        // Close modal if the dismissed appointment matches the current one being shown
        if (showModal && unconfirmedAppointment && data.appointmentId === unconfirmedAppointment.id) {
          console.log(`Dismissing appointment popup for appointment ${data.appointmentId}, reason: ${data.reason}`);
          closeModal();
          setUnconfirmedAppointment(null);

          // Show a toast notification explaining why it was dismissed
          if (data.reason === 'confirmed_by_another_technician') {
            toast.info('Este chamado foi aceito por outro técnico.', {
              position: "top-right",
              autoClose: 5000,
            });
          } else if (data.reason === 'reassigned_by_admin') {
            toast.info('Este chamado foi reatribuído pelo administrador.', {
              position: "top-right",
              autoClose: 5000,
            });
          }
        }
        // Always re-poll to update the list
        pollUnconfirmedAppointmentsHandler();
      });

      return () => {
        window.removeEventListener('newAppointmentForTechnician', handleNewAppointmentEvent);
        window.removeEventListener('appointmentUpdatedForTechnician', handleAppointmentUpdatedEvent);
        unsub2();
        unsub3();
        unsub4();
      };
    }
  }, [user, showModal, location.pathname]);

  // Admin notifications (profile_review_needed, appointment_approval_request)
  // are now handled globally in NotificationContext

  // Technician notifications (appointment_approved, appointment_rejected)
  // are now handled globally in NotificationContext

  useEffect(() => {
    pollUnconfirmedAppointmentsHandler();
  }, [rePollUnconfirmedAppointments]);

  // Socket join is handled in AppContent

  // Debug modal render conditions
  React.useEffect(() => {
    console.log('🎭 [MODAL RENDER DEBUG] Render conditions:', {
      showModal,
      hasUserId: !!user?.id,
      userId: user?.id,
      userRole: user?.role,
      isTechnician: user?.role === 'technician',
      hasUnconfirmedAppointment: !!unconfirmedAppointment,
      unconfirmedAppointmentId: unconfirmedAppointment?.id,
      isNotArray: !Array.isArray(unconfirmedAppointment),
      shouldRenderModal: showModal && user?.id && user?.role === 'technician' && unconfirmedAppointment && !Array.isArray(unconfirmedAppointment),
    });
  }, [showModal, user?.id, user?.role, unconfirmedAppointment]);

  return (
    <>
      <PreprodBanner />
      <ServerErrorIndicator />
      <AppContent />
      {showModal
        && user?.id
        && user?.role === 'technician'
        && unconfirmedAppointment
        && !Array.isArray(unconfirmedAppointment)
        && <Modal appointment={unconfirmedAppointment} />}
      <OfflineIndicator />
      <PWAInstallPrompt />
      <UpdateNotification
        isVisible={showUpdateNotification}
        onUpdate={performUpdate}
        onDismiss={dismissUpdate}
      />
{/* <ServiceWorkerDebug /> */}
      <ToastContainer />
    </>
  );
};

const App: React.FC = () => {
  return (
    <ServerStatusProvider>
      <UserProvider>
        <NotificationProvider>
          <ModalProvider>
            <Router basename={process.env.REACT_APP_HOMEPAGE || '/manu'}>
              <AppWrapper />
            </Router>
          </ModalProvider>
        </NotificationProvider>
      </UserProvider>
    </ServerStatusProvider>
  );
};

export default App;
